import { host } from '../env-values';
import { ModelStatus } from '../types/enum/model-status';
import { ProjectStatus } from '../types/enum/project-status';
import { uriWithQueryParams } from './utils/uri-with-query-params';

/* Account API */
const accountsRootUri = `${host}/accounts/`;

const accountListUri = (queryParams?: {
  username?: string;
  email?: string;
  isActive?: boolean;
  search?: string;
  ordering?: string; // Which field to use when ordering the results
  page?: number;
  size?: number;
}) =>
  queryParams === undefined
    ? accountsRootUri
    : uriWithQueryParams(accountsRootUri, [
        ['username', queryParams.username],
        ['email', queryParams.email],
        ['is_active', queryParams.isActive],
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const accountByIdUri = (id: number) => `${accountsRootUri}${id}/`;

const loginUri = `${host}/login/`;
const logoutUri = `${host}/logout/`;

/* Exporter Csv API */

const exporterCsvsRootUri = `${host}/csvs/`;

const exporterCsvListUri = (queryParams?: {
  name?: string;
  search?: string;
  ordering?: string; // Which field to use when ordering the results
  page?: number;
  size?: number;
}) =>
  queryParams === undefined
    ? exporterCsvsRootUri
    : uriWithQueryParams(exporterCsvsRootUri, [
        ['name', queryParams.name],
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const exporterCsvByIdUri = (id: number) => `${exporterCsvsRootUri}${id}/`;

const exporterCsvDownloadResultUri = (
  csvId: number,
  fileType: 'CSV'
) =>
  uriWithQueryParams(`${exporterCsvByIdUri(csvId)}download`, [
    ['type', fileType]
  ]);

/* Koumuten API */

const koumutensRootUri = `${host}/construction-companies/`;

const koumutenListUri = (queryParams?: {
  name?: string;
  search?: string;
  ordering?: string; // Which field to use when ordering the results
  page?: number;
  size?: number;
}) =>
  queryParams === undefined
    ? koumutensRootUri
    : uriWithQueryParams(koumutensRootUri, [
        ['name', queryParams.name],
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const koumutenByIdUri = (koumutenId: number) =>
  `${koumutensRootUri}${koumutenId}/`;

/* Company API */
const companiesRootUri = `${host}/companies/`;

const companyListUri = (queryParams?: {
  koumutenId?: number;
  name?: string;
  search?: string;
  ordering?: string; // Which field to use when ordering the results
  page?: number;
  size?: number;
}) =>
  queryParams === undefined
    ? companiesRootUri
    : uriWithQueryParams(companiesRootUri, [
        ['construction_company_id', queryParams.koumutenId],
        ['name', queryParams.name],
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const companyListOfKoumutenUri = (koumutenId: number) =>
  companyListUri({ koumutenId });

const companyByIdUri = (companyId: number) =>
  `${companiesRootUri}${companyId}/`;

/* Upload Image API */
const filesUri = `${host}/files/`;
const fileByIdUri = (fileId: number) => `${filesUri}${fileId}/`;

/* Model API */
const modelsRootUri = `${host}/models/`;

const modelListUri = (queryParams?: {
  canByUsed?: boolean;
  companyId?: number;
  status?: ModelStatus;
  search?: string;
  ordering?: string; // Which field to use when ordering the results
  page?: number;
  size?: number;
}) =>
  queryParams === undefined
    ? modelsRootUri
    : uriWithQueryParams(modelsRootUri, [
        ['can_be_used', queryParams.canByUsed],
        ['company_id', queryParams.companyId],
        ['status', queryParams.status],
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const modelListOfCompanyUri = (companyId: number) =>
  modelListUri({ companyId });

const modelByIdUri = (modelId: number) => `${modelsRootUri}${modelId}/`;

const modelTrainUri = (modelId: number) => `${modelByIdUri(modelId)}train/`; // 学習実行

/* Model Image API */
const modelImagesRootUri = (modelId: number) =>
  `${modelByIdUri(modelId)}images/`;

const modelImageListUri = (
  modelId: number,
  queryParams?: {
    search?: string;
    ordering?: string; // Which field to use when ordering the results
    page?: number;
    size?: number;
  }
) =>
  queryParams === undefined
    ? modelImagesRootUri(modelId)
    : uriWithQueryParams(modelImagesRootUri(modelId), [
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const modelImageByIdUri = (modelId: number, imageId: number) =>
  `${modelImagesRootUri(modelId)}${imageId}/`;

const modelNextImageUri = (modelId: number, imageId: number) =>
  `${modelImageByIdUri(modelId, imageId)}next/`;

const modelPreviousImageUri = (modelId: number, imageId: number) =>
  `${modelImageByIdUri(modelId, imageId)}previous/`;

const modelImageSummaryUri = (modelId: number, imageId: number) =>
  `${modelImageByIdUri(modelId, imageId)}summary/`;

/* Model Annotation API */
const modelImageAnnotationsRootUri = (modelId: number, imageId: number) =>
  `${modelImageByIdUri(modelId, imageId)}annotations/`;

const modelImageAnnotationListUri = (
  modelId: number,
  imageId: number,
  queryParams?: {
    search?: string;
    ordering?: string; // Which field to use when ordering the results
    page?: number;
    size?: number;
  }
) =>
  queryParams === undefined
    ? modelImageAnnotationsRootUri(modelId, imageId)
    : uriWithQueryParams(modelImageAnnotationsRootUri(modelId, imageId), [
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const modelImageAnnotationByIdUri = (
  modelId: number,
  imageId: number,
  annotationUuid: string
) => `${modelImageAnnotationsRootUri(modelId, imageId)}${annotationUuid}/`;

/* Label API */
const modelLabelsRootUri = (modelId: number) =>
  `${modelByIdUri(modelId)}labels/`;

const modelLabelListUri = (
  modelId: number,
  queryParams?: {
    search?: string;
    ordering?: string; // Which field to use when ordering the results
    page?: number;
    size?: number;
  }
) =>
  queryParams === undefined
    ? modelLabelsRootUri(modelId)
    : uriWithQueryParams(modelLabelsRootUri(modelId), [
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const modelLabelByIdUri = (modelId: number, labelId: number) =>
  `${modelLabelsRootUri(modelId)}${labelId}/`;

/* Training API */
const modelTrainingRootUri = (modelId: number) =>
  `${modelByIdUri(modelId)}trainings/`;

const modelTrainingListUri = (
  modelId: number,
  queryParams?: {
    search?: string;
    ordering?: string; // Which field to use when ordering the results
    page?: number;
    size?: number;
  }
) =>
  queryParams === undefined
    ? modelTrainingRootUri(modelId)
    : uriWithQueryParams(modelTrainingRootUri(modelId), [
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const modelTrainingByIdUri = (modelId: number, trainingId: number) =>
  `${modelTrainingRootUri(modelId)}${trainingId}/`;

/* Project API */
const projectsRootUri = `${host}/projects/`;

const projectListUri = (queryParams?: {
  companyId?: number;
  status?: ProjectStatus;
  authorId?: number;
  approverId?: number;
  search?: string;
  ordering?: string; // Which field to use when ordering the results
  page?: number;
  size?: number;
}) =>
  queryParams === undefined
    ? projectsRootUri
    : uriWithQueryParams(projectsRootUri, [
        ['company_id', queryParams.companyId],
        ['status', queryParams.status],
        ['author_id', queryParams.authorId],
        ['approver_id', queryParams.approverId],
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const projectListOfCompanyUri = (companyId: number) =>
  projectListUri({ companyId });

const projectByIdUri = (projectId: number) => `${projectsRootUri}${projectId}/`;

const projectDownloadResultImageUri = (
  projectId: number,
  fileType: 'PDF' | 'CSV'
) =>
  // `${projectByIdUri(projectId)}download/?type=${fileType}`
  uriWithQueryParams(`${projectByIdUri(projectId)}download`, [
    ['type', fileType]
  ]);

const projectApproveUri = (projectId: number) =>
  `${projectByIdUri(projectId)}approve/`;
const projectUnapproveUri = (projectId: number) =>
  `${projectByIdUri(projectId)}unapprove/`;

const projectSummaryUri = (projectId: number) =>
  `${projectByIdUri(projectId)}summary/`;

/* Project Image API */
const projectImagesRootUri = (projectId: number) =>
  `${projectByIdUri(projectId)}images/`;

const projectImageListUri = (
  projectId: number,
  queryParams?: {
    search?: string;
    ordering?: string; // Which field to use when ordering the results
    page?: number;
    size?: number;
  }
) =>
  queryParams === undefined
    ? projectImagesRootUri(projectId)
    : uriWithQueryParams(projectImagesRootUri(projectId), [
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const projectImageByIdUri = (projectId: number, imageId: number) =>
  `${projectImagesRootUri(projectId)}${imageId}/`;

const projectImagePredictUri = (projectId: number, imageId: number) =>
  `${projectImageByIdUri(projectId, imageId)}predict/`;

const projectNextImageUri = (projectId: number, imageId: number) =>
  `${projectImageByIdUri(projectId, imageId)}next/`;

const projectPreviousImageUri = (projectId: number, imageId: number) =>
  `${projectImageByIdUri(projectId, imageId)}previous/`;

const projectImageSummaryUri = (projectId: number, imageId: number) =>
  `${projectImageByIdUri(projectId, imageId)}summary/`;

/* Project Annotation API */
const projectImageAnnotationsRootUri = (projectId: number, imageId: number) =>
  `${projectImageByIdUri(projectId, imageId)}annotations/`;

const projectImageAnnotationListUri = (
  projectId: number,
  imageId: number,
  queryParams?: {
    search?: string;
    ordering?: string; // Which field to use when ordering the results
    page?: number;
    size?: number;
  }
) =>
  queryParams === undefined
    ? projectImageAnnotationsRootUri(projectId, imageId)
    : uriWithQueryParams(projectImageAnnotationsRootUri(projectId, imageId), [
        ['search', queryParams.search],
        ['ordering', queryParams.ordering],
        ['page', queryParams.page],
        ['size', queryParams.size]
      ]);

const projectImageAnnotationByIdUri = (
  projectId: number,
  imageId: number,
  annotationUuid: string
) => `${projectImageAnnotationsRootUri(projectId, imageId)}${annotationUuid}/`;

export const uri = {
  accountList: accountListUri,
  accountById: accountByIdUri,
  login: loginUri,
  logout: logoutUri,
  koumutenList: koumutenListUri,
  koumutenById: koumutenByIdUri,
  companyList: companyListUri,
  companyListOfKoumuten: companyListOfKoumutenUri,
  companyById: companyByIdUri,
  files: filesUri,
  fileById: fileByIdUri,
  modelList: modelListUri,
  modelListOfCompany: modelListOfCompanyUri,
  modelById: modelByIdUri,
  modelTrain: modelTrainUri,
  modelImageList: modelImageListUri,
  modelImageById: modelImageByIdUri,
  modelNextImage: modelNextImageUri,
  modelPreviousImage: modelPreviousImageUri,
  modelImageSummary: modelImageSummaryUri,
  modelImageAnnotationList: modelImageAnnotationListUri,
  modelImageAnnotationById: modelImageAnnotationByIdUri,
  modelLabelList: modelLabelListUri,
  modelLabelById: modelLabelByIdUri,
  modelTrainingList: modelTrainingListUri,
  modelTrainingById: modelTrainingByIdUri,
  projectList: projectListUri,
  projectListOfCompany: projectListOfCompanyUri,
  projectById: projectByIdUri,
  projectDownloadResultImage: projectDownloadResultImageUri,
  projectApprove: projectApproveUri,
  projectUnapprove: projectUnapproveUri,
  projectSummary: projectSummaryUri,
  projectImageList: projectImageListUri,
  projectImageById: projectImageByIdUri,
  projectImagePredict: projectImagePredictUri,
  projectNextImage: projectNextImageUri,
  projectPreviousImage: projectPreviousImageUri,
  projectImageSummary: projectImageSummaryUri,
  projectImageAnnotationList: projectImageAnnotationListUri,
  projectImageAnnotationById: projectImageAnnotationByIdUri,
  exporterCsvList: exporterCsvListUri,
  exporterCsvDownloadResult: exporterCsvDownloadResultUri
};
